import { useState, useEffect } from 'react';
import type { Skill, CreateSkillRequest, UpdateSkillRequest } from './skill-types';
import * as skillApi from './skill-api';

/**
 * Hook for workspace-scoped skill CRUD operations.
 */
export function useSkills(workspaceId?: string | null, autoLoad = true) {
  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const loadSkills = async () => {
    if (!workspaceId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await skillApi.fetchWorkspaceSkills(workspaceId);
      setSkills(result.skills);
    } catch (err) {
      setError(err as Error);
      console.error('Failed to load skills:', err);
    } finally {
      setLoading(false);
    }
  };

  const createSkill = async (data: CreateSkillRequest): Promise<Skill> => {
    if (!workspaceId) throw new Error('Workspace ID required to create a skill');
    setError(null);
    try {
      const skill = await skillApi.createSkill(workspaceId, data);
      setSkills((prev) => [skill, ...prev]);
      return skill;
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  const updateSkill = async (id: string, data: UpdateSkillRequest): Promise<Skill> => {
    if (!workspaceId) throw new Error('Workspace ID required to update a skill');
    setError(null);
    try {
      const skill = await skillApi.updateSkill(workspaceId, id, data);
      setSkills((prev) => prev.map((s) => (s.id === id ? skill : s)));
      return skill;
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  const deleteSkill = async (id: string): Promise<void> => {
    if (!workspaceId) throw new Error('Workspace ID required to delete a skill');
    setError(null);
    try {
      await skillApi.deleteSkill(workspaceId, id);
      setSkills((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  const importSkill = async (data: {
    skillName: string;
    files: Record<string, string>;
  }): Promise<Skill> => {
    if (!workspaceId) throw new Error('Workspace ID required to import a skill');
    setError(null);
    try {
      const skill = await skillApi.importSkill(workspaceId, data);
      setSkills((prev) => [skill, ...prev]);
      return skill;
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  const importSkillZip = async (file: File, skillName?: string): Promise<Skill> => {
    if (!workspaceId) throw new Error('Workspace ID required to import a skill');
    setError(null);
    try {
      const skill = await skillApi.importSkillZip(workspaceId, file, skillName);
      setSkills((prev) => [skill, ...prev]);
      return skill;
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  useEffect(() => {
    if (autoLoad && workspaceId) {
      loadSkills();
    }
  }, [autoLoad, workspaceId]);

  return {
    skills,
    loading,
    error,
    loadSkills,
    createSkill,
    updateSkill,
    deleteSkill,
    importSkill,
    importSkillZip,
  };
}
